import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Stack,
  Tooltip,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import api from "../../utils/api";
import { ThemeContext } from "../../utils/ThemeContext";

const MODE_OPTIONS = [
  {
    value: "upsert",
    label: "Upsert",
    icon: MergeTypeIcon,
    color: "#2f63ad",
    description: "Update existing rows and insert new ones (recommended)",
  },
  {
    value: "append",
    label: "Append",
    icon: AddCircleOutlineIcon,
    color: "#1a8a5c",
    description: "Insert new rows only. skip rows whose key already exists",
  },
  {
    value: "replace",
    label: "Replace",
    icon: DeleteSweepIcon,
    color: "#c0392b",
    description: "Delete ALL existing rows first, then insert. Use with caution!",
  },
];

const Uploads = () => {
  const { isDark } = useContext(ThemeContext);

  const [tables, setTables]           = useState([]);       // from /api/bulk-upload/status
  const [uploadType, setUploadType]   = useState("");       // selected slug
  const [mode, setMode]               = useState("upsert");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [result, setResult]           = useState(null);     // success response
  const [rowErrors, setRowErrors]     = useState([]);       // validation errors[]
  const [snack, setSnack]             = useState({ open: false, message: "", severity: "success" });

  // Load supported tables on mount
  useEffect(() => {
    api.get("/bulk-upload/status")
      .then((res) => setTables(res.data?.tables || []))
      .catch(() => setTables([]));
  }, []);

  const selectedTable = tables.find((t) => t.slug === uploadType) || null;

  const handleUploadTypeChange = (e) => {
    setUploadType(e.target.value);
    setSelectedFile(null);
    setResult(null);
    setRowErrors([]);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setSnack({ open: true, message: "Only .csv files are accepted.", severity: "error" });
      return;
    }
    setSelectedFile(f);
    setResult(null);
    setRowErrors([]);
  };

  const handleDownloadTemplate = async () => {
    if (!uploadType) return;
    try {
      const res = await api.get(`/bulk-upload/template/${uploadType}`, { responseType: "blob" });
      const url  = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href     = url;
      link.download = `${uploadType}_template.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnack({ open: true, message: "Template file not available on server.", severity: "warning" });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadType) return;
    setUploading(true);
    setResult(null);
    setRowErrors([]);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const res = await api.post(
        `/bulk-upload/${uploadType}?mode=${mode}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(res.data);
      setSelectedFile(null);
      setSnack({ open: true, message: "Upload complete!", severity: "success" });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors?.length) {
        setRowErrors(data.errors);
        setSnack({ open: true, message: data.message || "Validation errors in CSV.", severity: "error" });
      } else {
        setSnack({
          open: true,
          message: data?.message || "Upload failed. Please check your file and try again.",
          severity: "error",
        });
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────────
  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const cardSx = {
    borderRadius: "16px",
    border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
    p: { xs: 2, md: 2.5 },
  };

  const infoBgSx = {
    px: 1.5,
    py: 1.5,
    borderRadius: "12px",
    backgroundColor: isDark ? "rgba(47,99,173,0.12)" : "rgba(47,99,173,0.04)",
    border: isDark ? "1px solid rgba(47,99,173,0.2)" : "1px solid rgba(47,99,173,0.08)",
  };

  const headingSx = {
    fontWeight: 700,
    color: isDark ? "#dde5f0" : "#223142",
    mb: 0.5,
  };

  const subSx = { color: isDark ? "#8a9ab8" : "#6b778c", mb: 2 };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    borderColor: "rgba(47,99,173,0.25)",
    color: "#2f63ad",
    px: 2.25,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142", mb: 0.5 }}>
          Bulk Upload
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
          Import data via CSV files using the provided templates. Download a template, fill it in, then upload.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 2.5, maxWidth: 760 }}>

        {/* ── Step 1. Select type ─────────────────────────────────────────── */}
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="h6" sx={headingSx}>1 · Select Data Type</Typography>
          <Typography variant="body2" sx={subSx}>
            Choose what kind of data you want to import.
          </Typography>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Data Type</InputLabel>
            <Select value={uploadType} onChange={handleUploadTypeChange} label="Data Type">
              {tables.length === 0 ? (
                <MenuItem disabled>Loading…</MenuItem>
              ) : (
                tables.map((t) => (
                  <MenuItem key={t.slug} value={t.slug}>
                    {t.slug
                      .split("-")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedTable && (
            <Box sx={{ ...infoBgSx, mt: 2 }}>
              <Typography variant="body2" sx={{ color: isDark ? "#dde5f0" : "#223142", fontWeight: 600, mb: 0.75 }}>
                Required columns:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {selectedTable.requiredColumns.map((col) => (
                  <Chip
                    key={col}
                    label={col}
                    size="small"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.78rem",
                      backgroundColor: "rgba(47,99,173,0.12)",
                      color: "#2f63ad",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>

              {selectedTable.allColumns.length > selectedTable.requiredColumns.length && (
                <>
                  <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c", fontWeight: 600, mt: 1.25, mb: 0.75 }}>
                    Optional columns:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {selectedTable.allColumns
                      .filter((c) => !selectedTable.requiredColumns.includes(c))
                      .map((col) => (
                        <Chip
                          key={col}
                          label={col}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.78rem",
                            borderColor: "rgba(47,99,173,0.2)",
                            color: isDark ? "#8a9ab8" : "#6b778c",
                          }}
                        />
                      ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>

        {/* ── Step 2. Download template ───────────────────────────────────── */}
        {uploadType && (
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="h6" sx={headingSx}>2 · Download Template</Typography>
            <Typography variant="body2" sx={subSx}>
              Use this CSV template as the base for your import file.
            </Typography>

            <Box sx={{ ...infoBgSx, mb: 2 }}>
              <Typography variant="body2" sx={{ color: isDark ? "#dde5f0" : "#223142", mb: 0.5 }}>
                • The file must be a <strong>.csv</strong> file.
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? "#dde5f0" : "#223142", mb: 0.5 }}>
                • Keep the header row exactly as provided. do not rename columns.
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? "#dde5f0" : "#223142" }}>
                • Extra columns in the CSV are ignored automatically.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={secondaryButtonSx}
            >
              Download CSV Template
            </Button>
          </Paper>
        )}

        {/* ── Step 3. Choose mode ─────────────────────────────────────────── */}
        {uploadType && (
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="h6" sx={headingSx}>3 · Choose Upload Mode</Typography>
            <Typography variant="body2" sx={subSx}>
              Controls how existing records are handled.
            </Typography>

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_e, val) => { if (val) setMode(val); }}
              sx={{ flexWrap: "wrap", gap: 1, "& .MuiToggleButtonGroup-grouped": { border: "1px solid rgba(47,99,173,0.2) !important", borderRadius: "10px !important" } }}
            >
              {MODE_OPTIONS.map(({ value, label, icon: Icon, color }) => (
                <ToggleButton
                  key={value}
                  value={value}
                  sx={{
                    px: 2,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    color: isDark ? "#8a9ab8" : "#5f6b7a",
                    "&.Mui-selected": {
                      backgroundColor: `${color}18`,
                      color: color,
                      borderColor: `${color}55 !important`,
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 16, mr: 0.75 }} />
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Box sx={{ ...infoBgSx, mt: 1.5 }}>
              <Typography variant="body2" sx={{ color: isDark ? "#dde5f0" : "#223142" }}>
                <strong>{MODE_OPTIONS.find((m) => m.value === mode)?.label}:</strong>{" "}
                {MODE_OPTIONS.find((m) => m.value === mode)?.description}
              </Typography>
              {mode === "replace" && (
                <Typography variant="body2" sx={{ color: "#c0392b", fontWeight: 700, mt: 0.5 }}>
                  ⚠ This will permanently delete all existing records in this table before inserting.
                </Typography>
              )}
            </Box>
          </Paper>
        )}

        {/* ── Step 4. Upload file ─────────────────────────────────────────── */}
        {uploadType && (
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="h6" sx={headingSx}>4 · Upload File</Typography>
            <Typography variant="body2" sx={subSx}>
              Select your completed CSV file and upload it.
            </Typography>

            <Stack spacing={2} alignItems="flex-start">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="csv-file-input"
                key={uploadType}
              />

              <label htmlFor="csv-file-input">
                <Tooltip title="Choose a CSV file to upload" arrow>
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={primaryButtonSx}
                  >
                    Choose CSV File
                  </Button>
                </Tooltip>
              </label>

              {selectedFile ? (
                <Box
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "12px",
                    backgroundColor: isDark ? "rgba(47,99,173,0.12)" : "rgba(47,99,173,0.04)",
                    border: isDark ? "1px solid rgba(47,99,173,0.2)" : "1px solid rgba(47,99,173,0.08)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <DescriptionIcon sx={{ color: "#2f63ad" }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142" }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
                      {(selectedFile.size / 1024).toFixed(1)} KB · Ready to upload
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "12px",
                    border: "1px dashed rgba(47,99,173,0.2)",
                    backgroundColor: isDark ? "rgba(47,99,173,0.04)" : "rgba(47,99,173,0.02)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
                    No file selected yet.
                  </Typography>
                </Box>
              )}

              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                sx={{ ...primaryButtonSx, minWidth: 180 }}
              >
                {uploading ? "Uploading…" : "Upload File"}
              </Button>
            </Stack>
          </Paper>
        )}

        {/* ── Result summary ───────────────────────────────────────────────── */}
        {result && (
          <Paper elevation={0} sx={{ ...cardSx, border: "1px solid rgba(46,125,50,0.25)", backgroundColor: isDark ? "#0d2318" : "rgba(46,125,50,0.04)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <CheckCircleIcon sx={{ color: "#2e7d32" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                Upload Complete
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
              {[
                { label: "Total rows",  value: result.totalRows,  color: "#2f63ad" },
                { label: "Inserted",    value: result.inserted,   color: "#1a8a5c" },
                { label: "Updated",     value: result.updated,    color: "#d4811a" },
                { label: "Skipped",     value: result.skipped,    color: "#6b778c" },
              ].map(({ label, value, color }) => (
                <Box
                  key={label}
                  sx={{
                    px: 1.75,
                    py: 1,
                    borderRadius: "10px",
                    backgroundColor: `${color}14`,
                    border: `1px solid ${color}30`,
                    textAlign: "center",
                    minWidth: 80,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                    {value ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {result.warning && (
              <Typography variant="body2" sx={{ color: "#c0392b", fontWeight: 600 }}>
                ⚠ {result.warning}
              </Typography>
            )}
          </Paper>
        )}

        {/* ── Row-level errors ─────────────────────────────────────────────── */}
        {rowErrors.length > 0 && (
          <Paper elevation={0} sx={{ ...cardSx, border: "1px solid rgba(192,57,43,0.25)", backgroundColor: isDark ? "#2a0d0d" : "rgba(192,57,43,0.04)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
              <ErrorOutlineIcon sx={{ color: "#c0392b" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#c0392b" }}>
                Validation Errors ({rowErrors.length})
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c", mb: 1.25 }}>
              Fix these issues in your CSV and re-upload.
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
              {rowErrors.map((err, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", color: isDark ? "#e57373" : "#c0392b" }}>
                    • {err}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Uploads;
